import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { debounce } from '@mui/material';

import { useAssets } from '@labradorsports/assets';
import { HighlightedSearchText, PlayTagDisplay, StyledTextField } from '@labradorsports/components';

import { useNavigateWithQuery } from '../../shared/providers/index.js';
import { RouterSelectors, useSearchCommunityQuery } from '../../store/index.js';

interface Props {
    position: 'left' | 'right';
}

const CommunitySearch: FC<Props> = ({ position }) => {
    const { SearchEmpty } = useAssets();
    const navigate = useNavigateWithQuery();
    const [search, setSearch] = useState('');
    const [loadedSearch, setLoadedSearch] = useState('');
    const searchboxRef = useRef(null);
    const pageNumber = useSelector(RouterSelectors.communityPageNumber);
    const { data: { results: searchResults } = {} } = useSearchCommunityQuery(
        { query: loadedSearch, page: pageNumber },
        { skip: loadedSearch === '' }
    );

    const debouncedSearchCommunity = useCallback(
        debounce(async (str: string) => {
            setLoadedSearch(str);
        }, 500),
        []
    );

    const searchChange = (evt) => {
        setSearch(evt.target.value);

        debouncedSearchCommunity(evt.target.value);
    };

    const searchResultSelect = (id: string) => () => {
        setSearch('');
        setLoadedSearch('');
        navigate('/community/play', {
            communityPlayId: id,
        });
    };

    const keyDown = (evt: any) => {
        if (evt.key === 'Enter' && search !== '') {
            setLoadedSearch('');
            navigate('/community/search', {
                communitySearchText: search,
            });
        }
    };

    const searchboxRect = searchboxRef.current?.getBoundingClientRect();

    return (
        <div className="community-search">
            <StyledTextField
                label="Search"
                size="small"
                value={search}
                onChange={searchChange}
                onKeyDown={keyDown}
                inputProps={{ ref: searchboxRef }}
            />
            {searchResults && searchboxRect && loadedSearch !== ''
                ? createPortal(
                      <div
                          className="community-search-results-container"
                          style={{
                              top: searchboxRect.top + searchboxRect.height + 5,
                              left: position === 'right' ? searchboxRect.left : undefined,
                              right:
                                  position === 'left'
                                      ? window.innerWidth - (searchboxRect.left + searchboxRect.width)
                                      : undefined,
                          }}
                      >
                          <div className="community-search-results">
                              {searchResults.map((hit: any) => {
                                  const matchedTags = hit._highlightResult.tags
                                      .filter((result) => result.matchLevel !== 'none')
                                      .map((result) => result.value.replace('<em>', '').replace('</em>', ''));

                                  return (
                                      <div
                                          className="community-search-result"
                                          onClick={searchResultSelect(hit.objectID)}
                                          key={hit.objectID}
                                      >
                                          <div className="d-flex">
                                              <div className="flex-grow-1">
                                                  <HighlightedSearchText text={hit._highlightResult.name.value} />
                                              </div>
                                              <span className="small text-muted">
                                                  <HighlightedSearchText text={hit._highlightResult.sharedBy.value} />
                                              </span>
                                          </div>
                                          <PlayTagDisplay tags={hit.tags} highlightTags={matchedTags} />
                                          <HighlightedSearchText text={hit._highlightResult.description.value} />
                                      </div>
                                  );
                              })}
                              {searchResults.length === 0 ? (
                                  <div className="p-2">
                                      <img className="empty-results" src={SearchEmpty} alt="Empty search icon" />
                                      No results
                                  </div>
                              ) : null}
                          </div>
                      </div>,
                      document.getElementById('react')
                  )
                : null}
        </div>
    );
};

export default CommunitySearch;
